import NiceModal from '@ebay/nice-modal-react';
import { getCookieData } from '@repo/common-utils';
import { MAINTENANCE_SETTING_COOKIE } from '@repo/platform';
import { useUserEventLogger } from '@repo/tracking-service';
import { useDialog } from '@repo/ui';
import { useAppSignInContext } from '@repo/user-session';
import { Divider, List, Title, useToast } from '@zenterprise-inc/ui';
import cn from 'clsx';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { Dispatch, SetStateAction, useEffect, useRef } from 'react';
import { useLazyLoadQuery, useRelayEnvironment } from 'react-relay';

import { homeContentQuery, homeContentQuery$data } from '@/__generated__/homeContentQuery.graphql';
import { initCorporationRefundData } from '@/components/tax-refund/common/data/corporation-refund-data';
import { initIndisSearchRefundData } from '@/components/tax-refund/common/data/indis-search-refund-data';
import { setIndividualRefundData } from '@/components/tax-refund/common/data/individual-refund-data';
import { checkSearchStatus } from '@/components/tax-refund/common/graphql/query/checkSearchStatus';
import CorpsSection from '@/components/tax-refund/summary/CorpsSection';
import IndisSection from '@/components/tax-refund/summary/IndisSection';
import { RefundGuideButtonModal } from '@/components/tax-refund/summary/Modal/RefundGuideButtonModal';
import RefundSummaryCard from '@/components/tax-refund/summary/RefundSummaryCard';
import RetryBanner from '@/components/tax-refund/summary/RetryBanner';
import { SummaryBottomBanner } from '@/components/tax-refund/summary/SummaryBanners';
import { MarketingNotificationModal, useCloseAfterTwoWeeks } from '@/components/tax-refund/summary/marketing-notification-modal';
import s from '@/components/tax-refund/summary/summary.module.scss';
import { getHomeContentQuery } from '@/graphql/query/tax-refund/home';
import { SlotTargetType } from '@/lib/constants/ad-slots';
import { COMMON_PATHS, CORPORATION_PATHS, ERROR_PAGE_PATHS, INDIVIDUAL_PATHS, MY_PAGE_PATHS } from '@/lib/constants/paths';
import useDetectOs from '@/lib/hooks/use-detect-os';
import { checkHometaxAvailableTime } from '@/lib/utils/hometax-time';

interface Props {
  existDataType: string;
  setExistDataType: Dispatch<SetStateAction<string>>;
}

const SummaryContent = ({ existDataType, setExistDataType }: Props) => {
  const router = useRouter();
  const toast = useToast();
  const { showDialog } = useDialog();
  const { sendViewEvent, sendClickEvent, sendCustomActionEvent } = useUserEventLogger();
  const detectOS = useDetectOs();
  const { signRequiredStep } = useAppSignInContext();

  const relayEnvironment = useRelayEnvironment();

  const disableButton = useRef(false);

  // 마케팅 수신 동의 모달
  const isCloseAfterTwoWeeks = useCloseAfterTwoWeeks();

  const data: homeContentQuery$data = useLazyLoadQuery<homeContentQuery>(getHomeContentQuery, {}, { fetchPolicy: 'network-only' });

  useEffect(() => {
    const userData = data?.bznavRefund?.user;
    const hasIndis = !!userData?.indis || !!userData?.previousIndis || !!userData?.tritxIndis;
    setExistDataType(hasIndis && !!userData?.corps ? 'both' : hasIndis ? 'indis' : userData?.corps ? 'corps' : 'none');
  }, [data, setExistDataType]);

  useEffect(() => {
    const isDisagreeMarketingTerm = data.getRefundUserMarketingTerm && data.getRefundUserMarketingTerm.terms?.some((term) => !term.isApproved);

    if (isDisagreeMarketingTerm && isCloseAfterTwoWeeks && !signRequiredStep.ciAuthentication) {
      setTimeout(() => {
        if (window.location.pathname === COMMON_PATHS.환급조회목록) NiceModal.show(MarketingNotificationModal, { keepMounted: true });
      }, 1500);
    }
  }, [data, isCloseAfterTwoWeeks, signRequiredStep.ciAuthentication]);

  useEffect(() => {
    initIndisSearchRefundData();
    initCorporationRefundData();
  }, []);

  const navigateToAuthPage = (type: 'indis' | 'corps') => {
    if (type === 'indis') {
      setIndividualRefundData({
        corpType: 'INDIS',
        refundToken: '',
        serviceAgreementFlow: false
      });
      router.push({
        pathname: detectOS === 'windows' ? INDIVIDUAL_PATHS.개인조회방법선택 : COMMON_PATHS.간편인증_정보입력,
        query: { from: 'lookup' }
      });
    } else if (type === 'corps') {
      router.push({
        pathname: `${CORPORATION_PATHS.법인조회방법선택}`,
        query: router.query
      });
    }
  };

  const requestLookup = (type: 'indis' | 'corps') => {
    sendCustomActionEvent(
      'lookup_request',
      'start',
      {
        business: type
      },
      {
        isUseAirbridge: true,
        isUseGoogleAnalytics: true
      }
    );
    const maintenanceSetting = getCookieData(MAINTENANCE_SETTING_COOKIE);
    if (maintenanceSetting?.status && maintenanceSetting?.level === 'hometax') {
      sendViewEvent('home_dialog_home-tax-maintenance');
      showDialog({
        title: maintenanceSetting.title,
        description: maintenanceSetting.description,
        buttons: {
          primary: {
            onClick: () => {
              sendClickEvent('home_dialog_home-tax-maintenance');
            }
          }
        }
      });
      return;
    } else if (disableButton.current) return;
    disableButton.current = true;

    checkSearchStatus(relayEnvironment, type.toUpperCase() as 'INDIS' | 'CORPS').then((res) => {
      // 0-6시
      if (!checkHometaxAvailableTime()) {
        // 개인/법인 둘 중 하나라도 조회 이력 있음
        if (existDataType !== 'none') {
          sendViewEvent('home_dialog_time-block');
          showDialog({
            title: '아침 6시 이후에 조회해주세요',
            description: '매일 밤 0시부터 아침 6시까지 홈택스 점검으로 환급액 조회가 어려워요.',
            buttons: {
              primary: {
                onClick: () => {
                  sendClickEvent('home_dialog_time-block-confirm');
                }
              }
            }
          });
          disableButton.current = false;
          return;
        }
        router.push({
          pathname: ERROR_PAGE_PATHS.홈택스이용불가시간,
          query: { agreement: data.getRefundUserMarketingTerm.terms?.every((term) => term.isApproved) }
        });
        return;
      }

      if (res?.result === true && res?.progress) {
        if (res?.progress === '100') {
          // 조회 완료된 것 존재
          sendViewEvent('home_toast_lookup-error', {
            type: 'already-done'
          });
          toast({
            content: '조회 완료된 내역이 있어 먼저 불러올게요. 잠시만 기다려주세요.',
            icon: 'state-progress',
            iconColor: 'white',
            duration: 6000
          });

          setTimeout(() => {
            router.reload();
          }, 6000);
        } else {
          // 수집 중 인 것 존재
          sendViewEvent('home_toast_lookup-error', {
            type: 'collecting'
          });
          toast({
            content: '환급 조회 중인 내역이 있어요. 우선 조회를 위해 조회 화면으로 이동할게요.',
            icon: 'state-progress',
            iconColor: 'white',
            duration: 4000
          });

          setTimeout(() => {
            navigateToAuthPage(type);
          }, 4000);
        }
      } else {
        // 최초 조회
        navigateToAuthPage(type);
      }
    });
  };

  return (
    <>
      {existDataType !== 'checking' && (
        <div className={'flex w-full flex-col items-center'}>
          {(existDataType !== 'none' && (
            <div className={cn('page-padding w-full max-w-[520px] pb-6 pt-4', s.summary_main_section)}>
              <RetryBanner fragmentRef={data} />
              <div className={'flex flex-col gap-5'}>
                <IndisSection fragmentRef={data} onClickLookup={() => requestLookup('indis')} />
                <CorpsSection fragmentRef={data} onClickLookup={() => requestLookup('corps')} />
              </div>
              <div>
                <Divider.Line className={'my-4 bg-gray-200'} />
                <div className={'flex place-content-between gap-3'}>
                  <div className="inline-block w-full transition-transform duration-150 ease-in-out hover:scale-[0.97]">
                    <List.Basic
                      title={{ text: '환급 가이드', color: 'gray.800', titleStyle: 'text-15' }}
                      left={<Image src={'/images/illus-icons/fill-jotter-bl-rg.svg'} width={32} height={32} alt={'green money bag'} />}
                      className={'rounded-4 flex h-full min-h-[62px] w-full cursor-pointer items-center !bg-white px-4'}
                      onClick={() => {
                        sendClickEvent('home_body_refund-guide');
                        NiceModal.show(RefundGuideButtonModal, { keepMounted: true });
                      }}
                    />
                  </div>
                  <div className="inline-block w-full transition-transform duration-150 ease-in-out hover:scale-[0.97]">
                    <List.Basic
                      title={{ text: '환급받은 내역', color: 'gray.800', titleStyle: 'text-15' }}
                      left={<Image src={'/images/illus-icons/fill-moneybag-gn-rg.svg'} width={32} height={32} alt={'green money bag'} />}
                      className="rounded-4 flex h-full min-h-[62px] w-full cursor-pointer items-center !bg-white px-4"
                      onClick={() => {
                        sendClickEvent('home_body_history');
                        router.push({
                          pathname: MY_PAGE_PATHS.환급받은내역,
                          query: router.query
                        });
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )) || (
            <div className="page-padding w-full max-w-[520px]">
              <Title.Headline title={`어떤 숨은 환급금을\n조회하시겠어요?`} size="large" />
              <div className="mt-3 flex flex-col gap-y-4">
                <RefundSummaryCard
                  cardType="indis"
                  cardTitle={'개인·개인사업자'}
                  buttonState={{
                    title: '환급조회',
                    onClick: () => {
                      sendClickEvent('home_body_lookup-first', { business: 'indis' });
                      requestLookup('indis');
                    }
                  }}
                />
                <RefundSummaryCard
                  cardType="corps"
                  cardTitle={'법인사업자'}
                  buttonState={{
                    title: '환급조회',
                    onClick: () => {
                      sendClickEvent('home_body_lookup-first', { business: 'corps' });
                      requestLookup('corps');
                    }
                  }}
                />
              </div>
            </div>
          )}

          {existDataType !== 'none' && (
            <div className={cn('flex w-full justify-center', s.summary_bottom_section)}>
              <div className="page-padding flex w-full max-w-[520px] flex-col gap-4 rounded-t-[28px] bg-white pb-10 pt-2">
                {(existDataType === 'indis' || existDataType === 'corps') && (
                  <div>
                    <Title.Section title={`${existDataType === 'indis' ? '법인' : '개인·개인'}사업자도 있으신가요?`} size="small" />
                    <RefundSummaryCard
                      className={'!bg-gray-50'}
                      cardType={existDataType === 'indis' ? 'corps' : 'indis'}
                      cardTitle={existDataType === 'indis' ? '법인사업자' : '개인·개인사업자'}
                      buttonState={{
                        title: '환급조회',
                        onClick: () => {
                          sendClickEvent('home_body_lookup-other-business', {
                            businessType: existDataType === 'indis' ? 'corps' : 'indis'
                          });
                          requestLookup(existDataType === 'indis' ? 'corps' : 'indis');
                        }
                      }}
                    />
                  </div>
                )}
                <SummaryBottomBanner
                  slot={'HOME_BOTTOM_BENEFIT'}
                  corpType={existDataType === 'both' ? 'INDIS' : (existDataType.toUpperCase() as SlotTargetType)}
                  title={'혜택·이벤트'}
                />
                <SummaryBottomBanner
                  slot={'HOME_BOTTOM_HELP'}
                  corpType={existDataType === 'both' ? 'INDIS' : (existDataType.toUpperCase() as SlotTargetType)}
                  title={'다른 도움이 필요하신가요?'}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default SummaryContent;
